import "./campaigns.css";
import Page from "../../components/page/Page";
import React, {useEffect, useState} from "react";
import useAppContext from "../../hooks/useAppContext";
import NewCampaignModal from "../../components/modals/campaignModals/NewCampaignModal";
import CampaignThumb from "../../components/campaignThumb/CampaignThumb";
import EmptyState from "../../components/emptyState/EmptyState";
import {Button} from "antd";
import {PlusIcon} from "../../assets/icons/PlusIcon";
import Skeleton from "../../components/skeleton/Skeleton";
import CampaignAnalyticsPanel from "../../components/campaignAnalyticsPanel/CampaignAnalyticsPanel";
import {getShopCampaignAverage} from "../../api/shopCampaignAverage";
import useUserContext from "../../hooks/useUserContext";
import {useLocation, useNavigate} from "react-router-dom";
import {getWhatsappProfile} from "../../api/getWhatsappProfile";
import {WarningIcon} from "../../assets/icons/WarningIcon";
import ReviewWarning from "../../components/reviewWarning/ReviewWarning";
import WhatsappIntegrationButton from "../../components/whatsappIntegrationButton/WhatsappIntegrationButton";
import useFlag from "../../hooks/useFlag";

export interface campaignAverageProps {
    clientsAvg: number;
    sentAvg: number;
    deliveredAvg: number;
    readAvg: number;
    clickedAvg: number;
    convertedAvg: number;
}

const Campaigns = () => {
    const { selectedDomain, onboardingStatus: { isFullyOnboarded, isOnboardingStatusLoaded } } = useUserContext();
    const { data: { templates, campaigns, waba }  } = useAppContext();
    const [isNewCampaignModalOpen, setIsNewCampaignModalOpen] = useState(false);
    const [campaignIdToEdit, setCampaignIdToEdit] = useState<number | undefined>(undefined);
    const isEnabled = isFullyOnboarded;
    const { isFlagOn } = useFlag();
    const isV2 = isFlagOn('v2');
    const [analyticsPanelId, setAnalyticsPanelId] = useState<number | undefined>(undefined);
    const [shopCampaignAverage, setShopCampaignAverage] = useState<campaignAverageProps | undefined>(undefined);
    const navigate = useNavigate();
    const [messagingLimit, setMessagingLimit] = useState<number | null>(null);
    const [messagingLimitSentence, setMessagingLimitSentence] = useState('');
    const resultsToShowStep = 5;
    const [resultsToShow, setResultsToShow] = useState(resultsToShowStep);

    const location = useLocation();

    useEffect(() => {
        const shouldOpenNewCampaignModal = location.state as { autoOpenNewCampaignModal?: boolean };
        if(shouldOpenNewCampaignModal) {
            setIsNewCampaignModalOpen(true);
        }
    }, [location.state])

    const getMessagingLimit = async () => {
        const response = await getWhatsappProfile(selectedDomain!);
        if(response.data?.properties?.messaging_limit) {
            const messagingLimitStr = response.data.properties.messaging_limit;

            if (!messagingLimitStr || messagingLimitStr === 'Unavailable') {
                setMessagingLimit(250);
                setMessagingLimitSentence("Your current messaging limit is 250 recipients per day. Complete your business verification and start messaging to increase your limit.");
            } else if (messagingLimitStr.includes('1K')) {
                setMessagingLimit(1000);
                setMessagingLimitSentence("You're currently limited to 1,000 recipients per day. Keep sending messages to increase your messaging tier.")
            } else if (messagingLimitStr.includes('10K')) {
                setMessagingLimit(10000);
                setMessagingLimitSentence("You're currently limited to 10,000 recipients per day. Consistent messaging will help you reach the next tier.");
            } else if (messagingLimitStr.includes('100K')) {
                setMessagingLimit(100000);
                setMessagingLimitSentence("You're currently limited to 100,000 recipients per day. You’re one step away from unlimited messaging.");
            } else if (messagingLimitStr.toLowerCase().includes('unlimited')) {
                setMessagingLimit(Infinity);
            } else {
                setMessagingLimit(250);
                setMessagingLimitSentence("Your current messaging limit is 250 recipients per day. Complete your business verification and start messaging to increase your limit.");
            }
        } else {
            setMessagingLimit(0);
            setMessagingLimitSentence("Messaging limit information is unavailable at the moment. Please try again later or contact support.");
        }
    }

    const openCampaign = (campaignId: number) => {
        setCampaignIdToEdit(campaignId);
    }

    const openAnalytics = (campaignId: number) => {
        setAnalyticsPanelId(campaignId);
    }

    const closeAnalytics = () => setAnalyticsPanelId(undefined);

    const loadShopCampaignAverages = async () => {
        if(!selectedDomain) return;
        const campaignAvg = await getShopCampaignAverage(selectedDomain);
        setShopCampaignAverage(campaignAvg.data);
    }

    useEffect(() => {
        if((isNewCampaignModalOpen || campaignIdToEdit) && !!analyticsPanelId) {
            closeAnalytics();
        }
    }, [isNewCampaignModalOpen, campaignIdToEdit]);

    useEffect(() => {
        if(selectedDomain && selectedDomain !== 'vuelve.co') {
            loadShopCampaignAverages();
            getMessagingLimit();
        }
    }, [selectedDomain]);

    if(!isOnboardingStatusLoaded) return null;

    return (
        <Page
            className="campaigns-container"
            title="Campaigns"
            subtitle="Create and send one-time, on-demand messages to your customers with manual campaigns. Unlike automated workflows, which are triggered by specific events (such as an abandoned cart), manual campaigns allow you to send tailored messages whenever you choose. This tool is perfect for personalized promotions, announcements, or any other communication that doesn’t require event-based triggers."
            primaryAction={{
                label: 'Add Campaign',
                action: () => setIsNewCampaignModalOpen(true),
                isDisabled: !isEnabled,
            }}
            secondaryActions={[{
                label: 'Audience',
                action: () => navigate('/audience'),
                isDisabled: !isEnabled,
            }]}
        >
            {messagingLimitSentence ?
                <div className={`messaging-limit-note ml-${messagingLimit}`}>
                    <WarningIcon width={32} /><div>{messagingLimitSentence} <span onClick={() => navigate('/docs#messagingLimit')}>Learn More</span></div>
                </div>
            : null}
            <ReviewWarning />
            {!campaigns.data || campaigns.isLoading ? <Skeleton type="rows" /> : campaigns.data?.length ?
                <div>
                    <div className="campaigns-list">
                        {campaigns.data.slice(0, resultsToShow).map((campaign: any, index: number) => {
                            return <CampaignThumb totalCampaignsCount={campaigns.data.length} key={`campaign-thumb-${campaign.id}`} campaign={campaign} onCampaignClick={() => openCampaign(campaign.id)} index={index} onCampaignAnalyticsClick={openAnalytics} />
                        })}
                    </div>
                    {resultsToShow > campaigns?.data?.length ? null :
                        <div className="load-more-button-container">
                            <Button type="default" className="load-more-button"
                                onClick={() => setResultsToShow(resultsToShow + resultsToShowStep)}>Load More Campaigns</Button>
                        </div>
                    }
                </div>
            : <EmptyState
                    subTitle="Try creating a new campaign to reach your clients"
                    actions={isEnabled ? <Button type="primary" onClick={() => setIsNewCampaignModalOpen(true)} size="large" >Create a New Campaign</Button> : isV2 ? <Button type="primary" onClick={() => navigate('/vivo')} size="large" >Complete onboarding with Vivo</Button> : <WhatsappIntegrationButton /> }
                />}
            <NewCampaignModal isOpen={isNewCampaignModalOpen || !!campaignIdToEdit} onClose={() => {
                setIsNewCampaignModalOpen(false)
                setCampaignIdToEdit(undefined);
            }} campaignIdToEdit={campaignIdToEdit} />

            <CampaignAnalyticsPanel isOpen={!!analyticsPanelId} onClose={closeAnalytics} campaignId={analyticsPanelId} shopCampaignAverage={shopCampaignAverage} />
        </Page>
    )
}

export default Campaigns;